import React, { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { DropdownWithActions, CheckBoxOptionsList } from './Dropdown';
import { MultiSelectGroup } from '@xo-union/tk-component-switches';
import { PillButton } from './PillButton';
import { useHandleOutsideClick, PillDropdownOptions } from './index';

import styles from './styles.scss';
import Spinner from '@xo-union/tk-component-spinner';

interface PillWithCheckboxDropdownProps {
  filters?: string[];
  text: string;
  options: PillDropdownOptions;
  setValue: (newValue: string[]) => void;
  defaultValue: string[] | undefined;
  selected: boolean;
  onPillClick?: () => void;
  onSaveClick?: (newValues: string[]) => void;
  small?: boolean;
  isMobile?: boolean;
}

export const PillWithCheckboxDropdown = ({
  filters = [],
  text,
  options,
  setValue,
  defaultValue,
  selected: selectedActiveState,
  onPillClick,
  onSaveClick,
  small = false,
  isMobile = false,
  ...rest
}: PillWithCheckboxDropdownProps): JSX.Element | null => {
  const [showDrop, setShowDrop] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(defaultValue || []);
  const [saved, setSaved] = useState(defaultValue || []);
  const pillButtonRef = useRef<HTMLButtonElement>(null);

  useHandleOutsideClick({
    onOutsideAction: () => setShowDrop(false),
    elementRef: dropdownRef,
  });

  useEffect(() => {
    if (!showDrop && isEqual(selected, saved)) {
      setSelected(saved);
    }
  }, [defaultValue, saved, selected, selectedActiveState, showDrop]);

  useEffect(() => {
    setSelected(defaultValue || []);
    setSaved(defaultValue || []);
  }, [defaultValue, selectedActiveState]);

  const handleChange = (value: string[]) => {
    setSelected(value);
  };

  const handlePillClick = useCallback(() => {
    // remove blur for mobile
    pillButtonRef.current?.blur();
    // only on open clicks, not close clicks
    if (showDrop === false && !!onPillClick) onPillClick();
    setShowDrop(state => !state);
  }, [onPillClick, showDrop, pillButtonRef]);

  const handleOnSave = useCallback(
    (selectedValues: string[]) => {
      const newValues = filters.filter(value => !defaultValue?.includes(value));
      return onSaveClick ? onSaveClick([...newValues, ...selectedValues]) : null;
    },
    [filters, defaultValue, onSaveClick]
  );

  const handlePrimaryAction = useCallback(() => {
    setSaved(selected);
    setValue(selected);
    setShowDrop(false);
    handleOnSave(selected);
  }, [handleOnSave, selected, setValue]);

  const handleSecondaryAction = useCallback(() => {
    setSelected([]);
  }, []);

  return (
    <div ref={dropdownRef} className={styles.pillButtonWithDropdown}>
      <PillButton
        selected={showDrop || selectedActiveState}
        onClick={handlePillClick}
        aria-haspopup={true}
        aria-expanded={showDrop}
        ref={pillButtonRef}
        {...rest}
      >
        {saved.length > 0 ? `${text} (${saved.length})` : text}
      </PillButton>

      {showDrop && !isMobile ? (
        <DropdownWithActions
          primaryButtonText="Save"
          secondaryButtonText="Clear"
          primaryButtonAction={handlePrimaryAction}
          secondaryButtonAction={handleSecondaryAction}
          primaryButtonDisabled={selected === saved}
          small={small}
        >
          {options.length > 1 ? (
            <MultiSelectGroup values={selected} onValuesChange={handleChange}>
              <CheckBoxOptionsList options={options} />
            </MultiSelectGroup>
          ) : (
            <Spinner />
          )}
        </DropdownWithActions>
      ) : null}
    </div>
  );
};
